import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { notificationService } from '../services/notificationService';
import { useCommonAuth } from './CommonAuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  // State
  const [notifications, setNotifications] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({
    total: 0,
    THREAT: 0,
    VULNERABILITY: 0,
    WORKFLOW: 0,
    SYSTEM: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Refs for polling control
  const statsPollingIntervalRef = useRef(null);
  const fullPollingIntervalRef = useRef(null);
  const isStatsPollingRef = useRef(false);
  const isFullPollingRef = useRef(false);
  
  const { user, isAuthenticated } = useCommonAuth();

  // ============================================
  // BACKGROUND STATS POLLING (Layer 1)
  // ============================================
  
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const stats = await notificationService.getNotificationStats();
      setUnreadCounts(stats);
      return stats.total;
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
      return 0;
    }
  }, []);

  const pollStats = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    await fetchUnreadCounts();
  }, [isAuthenticated, user, fetchUnreadCounts]);

  const startStatsPolling = useCallback(() => {
    if (isStatsPollingRef.current) return; // Already polling
    
    console.log('🔄 Starting background stats polling (30s interval)');
    isStatsPollingRef.current = true;
    
    // Poll immediately on start
    pollStats();
    
    // Then poll every 30 seconds
    statsPollingIntervalRef.current = setInterval(() => {
      pollStats();
    }, 30000); // 30 seconds
  }, [pollStats]);

  const stopStatsPolling = useCallback(() => {
    if (!isStatsPollingRef.current) return;
    
    console.log('⏸️ Stopping background stats polling');
    isStatsPollingRef.current = false;
    
    if (statsPollingIntervalRef.current) {
      clearInterval(statsPollingIntervalRef.current);
      statsPollingIntervalRef.current = null;
    }
  }, []);

  // ============================================
  // DROPDOWN FULL POLLING (Layer 2)
  // ============================================
  
  const fetchNotifications = useCallback(async (category = null, isRead = null) => {
    setIsLoading(true);
    try {
      const data = await notificationService.getNotifications({
        category: category === 'ALL' ? null : category,
        is_read: isRead,
        limit: 50,
        offset: 0
      });
      setNotifications(data.notifications || []);
      return data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
      return { notifications: [] };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const pollFullNotifications = useCallback(async () => {
    if (!isAuthenticated || !user || !isDropdownOpen) return;
    const category = selectedCategory === 'ALL' ? null : selectedCategory;
    await fetchNotifications(category);
  }, [isAuthenticated, user, isDropdownOpen, selectedCategory, fetchNotifications]);

  const startFullPolling = useCallback(() => {
    if (isFullPollingRef.current) return; // Already polling
    
    console.log('🔄 Starting dropdown full polling (10s interval)');
    isFullPollingRef.current = true;
    
    // Poll immediately on start
    pollFullNotifications();
    
    // Then poll every 10 seconds
    fullPollingIntervalRef.current = setInterval(() => {
      pollFullNotifications();
    }, 10000); // 10 seconds
  }, [pollFullNotifications]);

  const stopFullPolling = useCallback(() => {
    if (!isFullPollingRef.current) return;
    
    console.log('⏸️ Stopping dropdown full polling');
    isFullPollingRef.current = false;
    
    if (fullPollingIntervalRef.current) {
      clearInterval(fullPollingIntervalRef.current);
      fullPollingIntervalRef.current = null;
    }
  }, []);

  // ============================================
  // VISIBILITY CHANGE DETECTION (Layer 3)
  // ============================================
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - pause all polling
        console.log('👁️ Tab hidden - pausing all polling');
        stopStatsPolling();
        stopFullPolling();
      } else {
        // Tab is visible - resume polling
        console.log('👁️ Tab visible - resuming polling');
        if (isAuthenticated) {
          startStatsPolling();
          if (isDropdownOpen) {
            startFullPolling();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isDropdownOpen, startStatsPolling, stopStatsPolling, startFullPolling, stopFullPolling]);

  // ============================================
  // START/STOP STATS POLLING ON AUTH CHANGE
  // ============================================
  
  useEffect(() => {
    if (isAuthenticated && user && !document.hidden) {
      startStatsPolling();
    } else {
      stopStatsPolling();
    }

    // Cleanup on unmount
    return () => {
      stopStatsPolling();
    };
  }, [isAuthenticated, user, startStatsPolling, stopStatsPolling]);

  // ============================================
  // FETCH ONCE ON DROPDOWN OPEN (NO AUTO-POLLING)
  // ============================================
  
  useEffect(() => {
    if (isDropdownOpen && isAuthenticated && !document.hidden) {
      // Fetch once when dropdown opens
      const category = selectedCategory === 'ALL' ? null : selectedCategory;
      fetchNotifications(category);
    }
  }, [isDropdownOpen, isAuthenticated, selectedCategory, fetchNotifications]);

  // ============================================
  // ACTIONS
  // ============================================
  
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markNotificationAsRead(notificationId);
      
      // Optimistic update
      setNotifications(prev =>
        (prev || []).map(n =>
          n.notification_id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      
      // Update counts
      await fetchUnreadCounts();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  }, [fetchUnreadCounts]);

  const markAllAsRead = useCallback(async (category = null) => {
    try {
      await notificationService.markAllAsRead(category);
      
      // Optimistic update
      setNotifications(prev =>
        (prev || []).map(n =>
          (!category || n.category === category)
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      
      // Update counts
      await fetchUnreadCounts();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, [fetchUnreadCounts]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Remove from state
      setNotifications(prev =>
        (prev || []).filter(n => n.notification_id !== notificationId)
      );
      
      // Update counts
      await fetchUnreadCounts();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [fetchUnreadCounts]);

  const refreshNotifications = useCallback(async () => {
    const category = selectedCategory === 'ALL' ? null : selectedCategory;
    await fetchNotifications(category);
    await fetchUnreadCounts();
  }, [selectedCategory, fetchNotifications, fetchUnreadCounts]);

  const value = {
    notifications,
    unreadCounts,
    isLoading,
    selectedCategory,
    setSelectedCategory,
    isDropdownOpen,
    setIsDropdownOpen,
    fetchNotifications,
    fetchUnreadCounts,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    startStatsPolling,
    stopStatsPolling,
    startFullPolling,
    stopFullPolling
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
